import React, { useMemo, useState } from 'react';
import { IconButton, Tooltip } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { DataTable } from '../Common';
import { formatDate } from '../../helpers/UtilsFunctions';

const statusLabels = {
  active: 'Active',
  planned: 'Planned',
  completed: 'Completed',
  on_hold: 'On Hold',
};

const statusColors = {
  active: '#FFC107',
  planned: '#FF9800',
  completed: '#4CAF50',
  on_hold: '#9E9E9E',
};

const getStatusKey = (value) => {
  if (!value) return 'active';
  return String(value).toLowerCase();
};

const ProjectsListView = ({
  projects = [],
  mode,
  onRowClick,
  onEditProject,
  onDeleteProject,
  hasEditPermission,
  hasDeletePermission,
  loading = false,
}) => {
  const [searchTerm, setSearchTerm] = useState('');

  const clampHeight = 3 * 14 * 1.4;

  const descriptionClampStyles = useMemo(
    () => `
      .projects-list-description {
        display: -webkit-box;
        -webkit-line-clamp: 3;
        -webkit-box-orient: vertical;
        overflow: hidden;
        text-overflow: ellipsis;
        line-height: 1.4 !important;
        max-height: ${clampHeight}px !important;
        font-family: 'Urbanist', Arial, sans-serif !important;
        font-size: 13px !important;
        color: ${mode === 'dark' ? '#CCCCCC' : '#666666'} !important;
        word-break: break-word;
        margin: 0;
      }
      .projects-list-description * {
        font-family: 'Urbanist', Arial, sans-serif !important;
        font-size: 13px !important;
        line-height: 1.4 !important;
        color: ${mode === 'dark' ? '#CCCCCC' : '#666666'} !important;
        margin: 0 !important;
      }
    `,
    [mode, clampHeight]
  );

  const columns = useMemo(
    () => [
      {
        accessorKey: 'title',
        header: 'Project',
        size: 240,
        Cell: ({ row }) => (
          <span
            style={{
              color: mode === 'dark' ? '#FFFFFF' : '#222222',
              fontSize: '14px',
              fontWeight: 600,
              fontFamily: 'Urbanist, Arial, sans-serif',
              wordBreak: 'break-word',
              lineHeight: 1.4,
            }}
          >
            {row.original.title || 'Untitled Project'}
          </span>
        ),
      },
      {
        accessorKey: 'status',
        header: 'Status',
        size: 120,
        Cell: ({ row }) => {
          const statusKey = getStatusKey(row.original.status || row.original.project_status);
          return (
            <span
              style={{
                display: 'inline-block',
                padding: '4px 10px',
                borderRadius: '6px',
                fontSize: '12px',
                fontWeight: 600,
                color: '#FFFFFF',
                backgroundColor: statusColors[statusKey] || '#FFC107',
                textTransform: 'capitalize',
                fontFamily: 'Urbanist, Arial, sans-serif',
              }}
            >
              {statusLabels[statusKey] || 'Active'}
            </span>
          );
        },
      },
      {
        accessorKey: 'progress_percentage',
        header: 'Progress',
        size: 110,
        Cell: ({ row }) => (
          <span
            style={{
              color: mode === 'dark' ? '#FFFFFF' : '#222222',
              fontSize: '14px',
              fontWeight: 500,
              fontFamily: 'Urbanist, Arial, sans-serif',
            }}
          >
            {(row.original.progress_percentage ?? 0) + '%'}
          </span>
        ),
      },
      {
        accessorKey: 'start_date',
        header: 'Start Date',
        size: 140,
        Cell: ({ row }) => (
          <span
            style={{
              color: mode === 'dark' ? '#FFFFFF' : '#222222',
              fontSize: '14px',
              fontFamily: 'Urbanist, Arial, sans-serif',
            }}
          >
            {formatDate(row.original.start_date) || 'Not set'}
          </span>
        ),
      },
      {
        accessorKey: 'end_date',
        header: 'End Date',
        size: 140,
        Cell: ({ row }) => (
          <span
            style={{
              color: mode === 'dark' ? '#FFFFFF' : '#222222',
              fontSize: '14px',
              fontFamily: 'Urbanist, Arial, sans-serif',
            }}
          >
            {formatDate(row.original.end_date) || 'Not set'}
          </span>
        ),
      },
      {
        accessorKey: 'created_by_username',
        header: 'Created By',
        size: 180,
        Cell: ({ row }) => (
          <span
            style={{
              color: mode === 'dark' ? '#FFFFFF' : '#222222',
              fontSize: '14px',
              fontFamily: 'Urbanist, Arial, sans-serif',
            }}
          >
            {row.original.created_by_username || 'Unknown'}
          </span>
        ),
      },
      {
        accessorKey: 'description',
        header: 'Description',
        size: 280,
        Cell: ({ row }) => (
          <div
            className="projects-list-description"
            dangerouslySetInnerHTML={{
              __html: row.original.description || 'No description available',
            }}
          />
        ),
      },
      {
        id: 'actions',
        header: 'Actions',
        size: 110,
        enableSorting: false,
        Cell: ({ row }) => (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
            onClick={(event) => event.stopPropagation()}
          >
            {hasEditPermission && (
              <Tooltip title="Edit">
                <IconButton
                  size="small"
                  onClick={() => onEditProject?.(row.original)}
                  data-action-button
                  sx={{
                    color: mode === 'dark' ? '#26C3C0' : '#009391',
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {hasDeletePermission && (
              <Tooltip title="Delete">
                <IconButton
                  size="small"
                  onClick={() => onDeleteProject?.(row.original)}
                  data-action-button
                  sx={{
                    color: '#f44336',
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </div>
        ),
      },
    ],
    [mode, onEditProject, onDeleteProject, hasEditPermission, hasDeletePermission]
  );

  return (
    <>
      <style>{descriptionClampStyles}</style>
      <DataTable
        data={projects}
        columns={columns}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder="Search projects..."
        onRowClick={onRowClick}
        loading={loading}
        emptyMessage="No projects found"
        enableRowSelection={false}
        enableSorting
        enableGlobalFilter
        enableColumnFilters={false}
        enablePagination
        pageSize={10}
        pageSizeOptions={[5, 10, 25, 50]}
        hover
        striped
        border
        shadow={false}
        borderRadius="0"
        customStyles={{
          backgroundColor: mode === 'dark' ? '#2a2a2a' : '#ffffff',
          border: `1px solid ${mode === 'dark' ? '#444' : '#e0e0e0'}`,
        }}
      />
    </>
  );
};

export default ProjectsListView;


